import PropTypes from 'prop-types';

import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Button from '@zenbusiness/zui/Button';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import {
  SubscriptionResultButtonRow,
  SubscriptionResultRoot
} from './SubscriptionResultStep.styles';

/**
 * Shared success/error result step for subscription flows (cancel, restart).
 * Layout primitives use co-located styled Stacks; inner copy uses plain Stack gap.
 * @param {object} props - Component props
 * @param {boolean} props.success - Whether the flow succeeded
 * @param {string} props.successIllustrationSrc - Illustration URL/path for success
 * @param {string} props.errorIllustrationSrc - Illustration URL/path for error
 * @param {string} props.successHeadingMessageId - Message id for success heading
 * @param {string} props.successBodyMessageId - Message id for success body
 * @param {string} props.errorHeadingMessageId - Message id for error heading
 * @param {string} props.errorBodyMessageId - Message id for error body
 * @param {Array<{labelMessageId: string, onClick: Function, variant: string, sx?: object, clickstreamProps?: object}>} props.successButtons - Buttons for success state
 * @param {Array<{labelMessageId: string, onClick: Function, variant: string, sx?: object, clickstreamProps?: object}>} props.errorButtons - Buttons for error state
 * @param {number} [props.illustrationSize=120] - Illustration size in px
 * @param {object} [props.illustrationSx] - Optional sx for illustration
 * @param {object} [props.buttonRowSx] - Optional sx for button row
 * @returns {import('react').ReactElement}
 */
export function SubscriptionResultStep({
  success,
  successIllustrationSrc,
  errorIllustrationSrc,
  successHeadingMessageId,
  successBodyMessageId,
  errorHeadingMessageId,
  errorBodyMessageId,
  successButtons,
  errorButtons,
  illustrationSize = 120,
  illustrationSx,
  buttonRowSx
}) {
  const isSuccess = Boolean(success);
  const illustrationSrc = isSuccess ? successIllustrationSrc : errorIllustrationSrc;
  const headingId = isSuccess ? successHeadingMessageId : errorHeadingMessageId;
  const bodyId = isSuccess ? successBodyMessageId : errorBodyMessageId;
  const buttons = isSuccess ? successButtons : errorButtons;

  return (
    <SubscriptionResultRoot>
      <SharedAsset
        alt=""
        src={illustrationSrc}
        width={illustrationSize}
        height={illustrationSize}
        sx={illustrationSx}
      />
      <Stack gap={1}>
        <Text size="2xl" weight="bold" family="secondary">
          <FormattedMessage id={headingId} />
        </Text>
        <Text size="md" color="secondary">
          <FormattedMessage id={bodyId} />
        </Text>
      </Stack>
      <SubscriptionResultButtonRow sx={buttonRowSx}>
        {buttons.map(({
          labelMessageId,
          onClick,
          variant,
          sx,
          clickstreamProps
        }) => (
          <Button
            key={labelMessageId}
            variant={variant}
            size="large"
            onClick={onClick}
            sx={sx}
            clickstreamProps={clickstreamProps}
          >
            <FormattedMessage id={labelMessageId} />
          </Button>
        ))}
      </SubscriptionResultButtonRow>
    </SubscriptionResultRoot>
  );
}

const buttonShape = PropTypes.shape({
  labelMessageId: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  variant: PropTypes.string.isRequired,
  sx: PropTypes.shape({}),
  clickstreamProps: PropTypes.shape({})
});

SubscriptionResultStep.propTypes = {
  success: PropTypes.bool.isRequired,
  successIllustrationSrc: PropTypes.string.isRequired,
  errorIllustrationSrc: PropTypes.string.isRequired,
  successHeadingMessageId: PropTypes.string.isRequired,
  successBodyMessageId: PropTypes.string.isRequired,
  errorHeadingMessageId: PropTypes.string.isRequired,
  errorBodyMessageId: PropTypes.string.isRequired,
  successButtons: PropTypes.arrayOf(buttonShape).isRequired,
  errorButtons: PropTypes.arrayOf(buttonShape).isRequired,
  illustrationSize: PropTypes.number,
  illustrationSx: PropTypes.shape({}),
  buttonRowSx: PropTypes.shape({})
};
